import React, { useState, useEffect } from 'react'
import Wrapper from '../../components/Wrapper'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  TablePagination,
  IconButton,
  Avatar,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import { menu, input, auto } from '../../translate'
import Api from '../../Api'
import { toast } from 'react-toastify'
import AssignedSearch from '../../components/new_component/AssignedSearch'
import { activitiesStyles, employeeTableStyles } from '../../helpers/style'

const { REACT_APP_SERVER } = process.env
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : '#7ebc0a'

export default function ConferenceCallList() {
  const navigate = useNavigate()
  const langIndex = +localStorage.getItem('atsLang') || 1
  const [conferenceCalls, setConferenceCalls] = useState([])
  const [filteredList, setFilteredList] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [tablePage, setTablePage] = useState(0)
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10)
  const [keepOnLeftMenu, setKeepOnLeftMenu] = useState(() => localStorage.getItem('conference_call_keep_on_left_menu') === 'true')

  const pageTitle = menu.conferenceCall?.[langIndex] || 'Conference call'

  useEffect(() => {
    fetchList()
  }, [])

  useEffect(() => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      const filtered = conferenceCalls.filter(
        (item) =>
          [item.name, item.virtual, item.autostatus ? auto.running[langIndex] : auto.paused[langIndex]].some(
            (f) => f != null && String(f).toLowerCase().includes(q)
          )
      )
      setFilteredList(filtered)
    } else {
      setFilteredList(conferenceCalls)
    }
    setTablePage(0)
  }, [searchQuery, conferenceCalls, langIndex])

  const fetchList = async () => {
    setLoading(true)
    try {
      const response = await Api.getConferenceCall()
      if (response?.data?.status === 'ok') {
        const data = response.data.auto || []
        setConferenceCalls(data)
        setFilteredList(data)
      }
    } catch (error) {
      console.error('Error fetching conference calls:', error)
      toast.error('Error fetching conference calls')
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (value) => setSearchQuery(value)
  const handleAdd = () => navigate('/conference_call_single/add')
  const handleEdit = (id) => navigate(`/conference_call_single/${id}`)
  const handleDelete = async (id) => {
    const msg =
      langIndex === 1 ? 'Are you sure you want to delete?' : langIndex === 2 ? 'Удалить?' : 'Ջնջե՞լ'
    if (!window.confirm(msg)) return
    try {
      await Api.deleteConferenceCall(id)
      setConferenceCalls((prev) => prev.filter((item) => item.id !== id))
      setFilteredList((prev) => prev.filter((item) => item.id !== id))
      toast.success('Deleted')
    } catch (error) {
      console.error('Error deleting:', error)
      toast.error('Error deleting')
    }
  }
  const handleStatus = async (id, status) => {
    const msg =
      langIndex === 1 ? 'Change status?' : langIndex === 2 ? 'Изменить статус?' : 'Փոխե՞լ կարգավիճակը'
    if (!window.confirm(msg)) return
    try {
      await Api.editConferenceCallStatus(id, { status: +status === 0 ? 1 : 0 })
      setConferenceCalls((prev) =>
        prev.map((item) => (item.id === id ? { ...item, autostatus: +status === 0 ? 1 : 0 } : item))
      )
      setFilteredList((prev) =>
        prev.map((item) => (item.id === id ? { ...item, autostatus: +status === 0 ? 1 : 0 } : item))
      )
      toast.success('Status updated')
    } catch (error) {
      console.error('Error updating status:', error)
      toast.error('Error updating status')
    }
  }
  const handleTableChangePage = (e, newPage) => setTablePage(newPage)
  const handleTableChangeRowsPerPage = (e) => {
    setTableRowsPerPage(parseInt(e.target.value, 10))
    setTablePage(0)
  }

  const paginatedList =
    tableRowsPerPage > 0
      ? filteredList.slice(tablePage * tableRowsPerPage, tablePage * tableRowsPerPage + tableRowsPerPage)
      : filteredList

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3, boxShadow: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              borderBottom: '2px solid rgba(0, 0, 0, 0.08)',
              pb: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                sx={{ color, fontSize: 18, '&:hover': { backgroundColor: `${color}15` } }}
                onClick={() => navigate('/short_function')}
                aria-label="Back"
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
              </IconButton>
              <Typography sx={{ fontWeight: 700, m: 0 }} variant="h5" component="h1">
                {pageTitle}
              </Typography>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={keepOnLeftMenu}
                    onChange={(e) => {
                      const v = e.target.checked
                      setKeepOnLeftMenu(v)
                      localStorage.setItem('conference_call_keep_on_left_menu', v ? 'true' : 'false')
                      if (v) localStorage.setItem('conference_call', 'true')
                      else localStorage.removeItem('conference_call')
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: color },
                    }}
                  />
                }
                label={langIndex === 1 ? 'Keep on the left menu' : langIndex === 2 ? 'Держать в левом меню' : 'Պահել ձախ մենյուում'}
              />
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <AssignedSearch
              value={searchQuery}
              onChange={handleSearchChange}
              onAdd={handleAdd}
              placeholder={input.search[langIndex]}
              showAdd={true}
              addColor={color}
            />
          </Box>
          {loading ? (
            <Box sx={employeeTableStyles.loadingBox}>
              <CircularProgress sx={{ color }} />
            </Box>
          ) : paginatedList.length > 0 ? (
            <>
              <Box sx={activitiesStyles.activitiesContainer}>
                {paginatedList.map((item) => {
                  const isRunning = item.autostatus
                  return (
                    <Card
                      key={item.id}
                      sx={{
                        ...activitiesStyles.activityCard,
                        '&:hover': { boxShadow: `0 8px 24px ${color}26`, borderColor: color },
                      }}
                    >
                      <CardContent sx={activitiesStyles.cardContent}>
                        <Box sx={activitiesStyles.cardContentBox}>
                          <Avatar
                            sx={{
                              ...activitiesStyles.getActivityAvatar(isRunning),
                              ...(isRunning && { bgcolor: color, boxShadow: `0 4px 12px ${color}4D` }),
                            }}
                          >
                            {isRunning ? <PauseIcon sx={{ fontSize: 28 }} /> : <PlayArrowIcon sx={{ fontSize: 28 }} />}
                          </Avatar>
                          <Box sx={activitiesStyles.activityContentBox}>
                            <Typography variant="h6" sx={activitiesStyles.activityTitle}>
                              {item.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {auto.line?.[langIndex] || 'Line'}: {item.virtual}
                            </Typography>
                          </Box>
                          <Box sx={employeeTableStyles.actionsContainer}>
                            {isRunning ? (
                              <CheckCircleIcon sx={employeeTableStyles.activeIcon(color)} />
                            ) : (
                              <CheckCircleOutlineIcon sx={{ color: '#9e9e9e', fontSize: 24 }} />
                            )}
                            <Box sx={employeeTableStyles.actionButtonsBox}>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(item.id)}
                                sx={{
                                  color,
                                  '&:hover': { backgroundColor: `${color}15`, transform: 'scale(1.1)' },
                                  transition: 'all 0.2s ease',
                                  padding: '6px',
                                }}
                                aria-label="Edit"
                              >
                                <FontAwesomeIcon icon={faPenToSquare} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(item.id)}
                                sx={employeeTableStyles.deleteIconButton}
                                aria-label="Delete"
                              >
                                <FontAwesomeIcon icon={faTrashCan} />
                              </IconButton>
                            </Box>
                            <Box
                              onClick={() => handleStatus(item.id, item.autostatus)}
                              sx={{
                                cursor: 'pointer',
                                mt: 0.5,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                display: 'inline-block',
                                ...(isRunning
                                  ? {
                                      color: '#fff',
                                      backgroundColor: color,
                                      animation: 'statusPulse 1.5s ease-in-out infinite',
                                    }
                                  : {
                                      backgroundColor: 'rgba(158, 158, 158, 0.1)',
                                      color: '#9e9e9e',
                                      transition: 'all 0.2s ease',
                                      '&:hover': { backgroundColor: `${color}15`, color },
                                    }),
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontSize: '0.65rem', fontWeight: 600, color: isRunning ? '#fff' : '#9e9e9e' }}
                              >
                                {item.autostatus ? auto.running[langIndex] : auto.paused[langIndex]}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )
                })}
              </Box>
              <Box sx={{ ...employeeTableStyles.footerBox, justifyContent: 'flex-end' }}>
                <TablePagination
                  component="div"
                  count={filteredList.length}
                  rowsPerPage={tableRowsPerPage}
                  page={tablePage}
                  onPageChange={handleTableChangePage}
                  onRowsPerPageChange={handleTableChangeRowsPerPage}
                  labelRowsPerPage={input.rowsPerPage[langIndex]}
                  rowsPerPageOptions={[10, 25, 100, { label: 'All', value: -1 }]}
                />
              </Box>
            </>
          ) : (
            <Box sx={employeeTableStyles.emptyStateBox}>
              <Typography variant="body1" color="text.secondary">
                {auto.no_found[langIndex]}
              </Typography>
            </Box>
          )}
        </Paper>
      </div>
    </Wrapper>
  )
}
